import { useEffect, useState } from 'react';

import type { SavedAnalysisListItem } from '@smartapply/shared';

import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError } from '../lib/api-client.js';
import { listAnalyses } from '../lib/analyses-client.js';
import { useRoute } from '../lib/route.js';

export function AnalysesRoute() {
  const { navigate } = useRoute();
  const [items, setItems] = useState<SavedAnalysisListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    listAnalyses()
      .then((res) => {
        if (alive) setItems(res.items);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao listar.');
      });
    return () => {
      alive = false;
    };
  }, []);

  function toggleSelect(id: string) {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1]!, id];
      return [...prev, id];
    });
  }

  function handleCompare() {
    if (selected.length !== 2) return;
    navigate('/comparar', { a: selected[0]!, b: selected[1]! });
  }

  function exitCompareMode() {
    setCompareMode(false);
    setSelected([]);
  }

  const canCompareAtAll = items !== null && items.length >= 2;

  return (
    <PageShell>
      <header className="mb-10">
        <p className="mb-4 inline-block">
          <Stamp>Histórico</Stamp>
        </p>
        <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px]">
          Análises salvas
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
          Análises de perfil que você salvou explicitamente. Por padrão, análises ficam só na
          sessão.
        </p>
      </header>

      {error && <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>}

      {items === null && !error && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      )}

      {items && items.length === 0 && (
        <p className="font-sans text-base text-[var(--ink-secondary)]">
          Nenhuma análise salva ainda.{' '}
          <a href="#/" className="text-[var(--accent-saffron)] underline underline-offset-4">
            Fazer uma análise
          </a>
          .
        </p>
      )}

      {canCompareAtAll && (
        <div className="mb-6 flex flex-wrap items-center gap-4">
          {!compareMode ? (
            <button
              type="button"
              onClick={() => setCompareMode(true)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-saffron)] hover:underline"
            >
              comparar análises
            </button>
          ) : (
            <>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                selecione 2 ({selected.length}/2)
              </p>
              <button
                type="button"
                onClick={handleCompare}
                disabled={selected.length !== 2}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-forest)] disabled:cursor-not-allowed disabled:text-[var(--ink-muted)]"
              >
                comparar →
              </button>
              <button
                type="button"
                onClick={exitCompareMode}
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
              >
                cancelar
              </button>
            </>
          )}
        </div>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-3">
          {items.map((item) => {
            const isSelected = selected.includes(item.id);
            return (
              <li
                key={item.id}
                className="card-paper rounded-sm"
                style={{
                  borderLeft:
                    item.criticalCount > 0
                      ? '3px solid var(--accent-crimson)'
                      : '3px solid var(--accent-saffron)',
                  outline:
                    compareMode && isSelected
                      ? '2px solid var(--accent-forest)'
                      : 'none',
                  outlineOffset: '4px',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (compareMode) toggleSelect(item.id);
                    else navigate(`/analises/${item.id}`);
                  }}
                  className="block w-full px-5 py-4 text-left"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                      {compareMode && (
                        <span
                          className="mr-2 inline-block h-3 w-3 rounded-sm border align-middle"
                          style={{
                            borderColor: 'var(--ink-tertiary)',
                            backgroundColor: isSelected
                              ? 'var(--accent-forest)'
                              : 'transparent',
                          }}
                        />
                      )}
                      <span className="tabular-nums">{item.findingsCount}</span> findings ·{' '}
                      <span
                        className="tabular-nums"
                        style={{
                          color: item.criticalCount > 0 ? 'var(--accent-crimson)' : 'inherit',
                        }}
                      >
                        {item.criticalCount} críticos
                      </span>{' '}
                      · {(item.durationMs / 1000).toFixed(1)}s
                    </p>
                    <span className="font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
                      {new Date(item.createdAt).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <p className="mt-1 font-serif text-base text-[var(--ink-primary)]">
                    {item.label || item.positioningSentence || '(sem rótulo)'}
                  </p>
                  <p className="mt-1 font-mono text-[10px] text-[var(--ink-muted)]">
                    modelo · {item.modelId}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </PageShell>
  );
}
